import { InputSignal, Signal, TemplateRef, Type } from '@angular/core';
import { AbstractMessageBar } from '../components/abstract/abstract-message-bar';

export type MessageBarConfig = SuccessMessageBarConfig | WarningMessageBarConfig | ErrorMessageBarConfig | InfoMessageBarConfig | LoadingMessageBarConfig | SavingMessageBarConfig | ProgressMessageBarConfig | ComponentMessageBarConfig<any> | TemplateMessageBarConfig<any>;

type ExtractGeneric<Type> = Type extends InputSignal<infer X> ? X : never;
type ChangeTypeOfKeys<T extends object> = {
  [K in keyof T]: T[K] extends InputSignal<any>
    ? ExtractGeneric<T[K]> | Signal<ExtractGeneric<T[K]>>
    : never;
};

type MessageBarData<T extends object> = ChangeTypeOfKeys<T>;

export interface MessageBarAction {
  title: Signal<string>;
  onExecute: () => void;
  type: 'link' | 'button';
}

export interface MessageBarConfigBase {
  title?: string | Signal<string>;
  message?: string | Signal<string>;
  icon?: string | Signal<string>;
  actions?: MessageBarAction[];
  duration?: number;
  dismiss?: boolean;
  onDismiss?(): void;
}

export interface SuccessMessageBarConfig extends MessageBarConfigBase {
  type: 'success';
}

export interface WarningMessageBarConfig extends MessageBarConfigBase {
  type: 'warning';
}

export interface ErrorMessageBarConfig extends MessageBarConfigBase {
  type: 'error';
}

export interface InfoMessageBarConfig extends MessageBarConfigBase {
  type: 'info';
}

export interface LoadingMessageBarConfig extends MessageBarConfigBase {
  type: 'loading';
  onDismiss?: () => void;
}

export interface SavingMessageBarConfig extends MessageBarConfigBase {
  type: 'saving';
  onDismiss?: () => void;
}

export interface ComponentMessageBarConfig<T extends object> extends MessageBarConfigBase {
  type: 'component';
  component: Type<AbstractMessageBar & T>;
  data: Partial<MessageBarData<T>>;
}

export interface TemplateMessageBarConfig<T extends object> extends MessageBarConfigBase {
  type: 'template';
  template: TemplateRef<T>;
  data: Partial<MessageBarData<T>>;
}

export interface MessageBarProgress {
  loaded: number | null;
  total: number | null;
  text: string | null;
  unit: string | 'byte' | '%' | null;
}

export interface ProgressMessageBarConfig extends MessageBarConfigBase {
  type: 'progress';
  progress?: Signal<MessageBarProgress>;
  onCancel?: () => void;
}
